import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, MapPin, StickyNote, User, Layers, Trash2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { activityService } from '@/lib/api/activity.service';
import { guestService } from '@/lib/api/guest.service';
import type { Activity, UpdateActivityDto } from '@/types/activity.types';
import type { Guest } from '@/types/guest.types';

interface ActivityExpandPanelProps {
  activity: Activity;
  eventId: number;
  isOpen: boolean;
  onToggle: () => void;
  onUpdated: (updated: Activity) => void;
  onDeleted: (id: number) => void;
  canManage: boolean;
}

// Format Date for datetime-local input (YYYY-MM-DDTHH:MM)
const toDatetimeLocal = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatTime = (iso?: string): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

export const ActivityExpandPanel: React.FC<ActivityExpandPanelProps> = ({
  activity,
  eventId,
  isOpen,
  onToggle,
  onUpdated,
  onDeleted,
  canManage,
}) => {
  const { t } = useTranslation(['activities', 'common']);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(activity.title);
  const [startTime, setStartTime] = useState(toDatetimeLocal(activity.start_time));
  const [endTime, setEndTime] = useState(toDatetimeLocal(activity.end_time));
  const [location, setLocation] = useState(activity.description ? '' : activity.location || '');
  const [description, setDescription] = useState(activity.description || '');
  const [parallelGroup, setParallelGroup] = useState(activity.parallel_group || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [leads, setLeads] = useState<Guest[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [loadingGuests, setLoadingGuests] = useState(false);

  // Reset edit fields when activity changes
  useEffect(() => {
    setTitle(activity.title);
    setStartTime(toDatetimeLocal(activity.start_time));
    setEndTime(toDatetimeLocal(activity.end_time));
    setLocation(activity.location || '');
    setDescription(activity.description || '');
    setParallelGroup(activity.parallel_group || '');
    setEditing(false);
  }, [activity]);

  // Load guests and leads when panel opens
  useEffect(() => {
    if (isOpen && canManage) {
      loadGuestsAndLeads();
    }
  }, [isOpen, activity.id]);

  const loadGuestsAndLeads = async () => {
    setLoadingGuests(true);
    try {
      const [guestList, activityData] = await Promise.all([
        guestService.getEventGuests(eventId),
        activityService.getActivity(activity.id),
      ]);
      setGuests(guestList);
      const actLeads = (activityData as any).leads || [];
      setLeads(actLeads);
      setSelectedLeadId(actLeads.length > 0 ? actLeads[0].id : null);
    } catch {
      // silently fail
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const updateData: UpdateActivityDto = {
        title: title.trim(),
        start_time: startTime ? new Date(startTime).toISOString() : null,
        end_time: endTime ? new Date(endTime).toISOString() : null,
        location: location.trim() || undefined,
        description: description.trim() || undefined,
        parallel_group: parallelGroup.trim() || null,
      };
      const updated = await activityService.updateActivity(activity.id, updateData);

      // Handle lead assignment change
      const currentLeadId = leads.length > 0 ? leads[0].id : null;
      if (selectedLeadId !== currentLeadId) {
        // Remove old lead
        if (currentLeadId) {
          await activityService.removeLead(activity.id, currentLeadId);
        }
        // Assign new lead
        if (selectedLeadId) {
          await activityService.assignLeads(activity.id, [selectedLeadId]);
        }
      }

      onUpdated(updated);
      setEditing(false);
    } catch {
      // keep editing open
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('activities:expandPanel.confirmDelete', { title: activity.title }))) return;
    setDeleting(true);
    try {
      await activityService.deleteActivity(activity.id);
      onDeleted(activity.id);
    } catch {
      // silently fail
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setTitle(activity.title);
    setStartTime(toDatetimeLocal(activity.start_time));
    setEndTime(toDatetimeLocal(activity.end_time));
    setLocation(activity.location || '');
    setDescription(activity.description || '');
    setParallelGroup(activity.parallel_group || '');
    setEditing(false);
  };

  const getStatusMeta = (status?: string): { label: string; className: string } => {
    const normalized = (status || 'planned').toLowerCase();

    switch (normalized) {
      case 'draft':
        return { label: 'draft', className: 'bg-gray-100 text-gray-600 border-gray-300' };
      case 'planning':
        return { label: 'planning', className: 'bg-sky-100 text-sky-700 border-sky-300' };
      case 'planned':
        // Backend stores "planned"; product language expects "planning".
        return { label: 'planning', className: 'bg-blue-100 text-blue-700 border-blue-300' };
      case 'ready':
        return { label: 'ready', className: 'bg-indigo-100 text-indigo-700 border-indigo-300' };
      case 'active':
        return { label: 'active', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
      case 'completed':
        return { label: 'completed', className: 'bg-green-100 text-green-700 border-green-300' };
      case 'skipped':
        return { label: 'skipped', className: 'bg-amber-100 text-amber-700 border-amber-300' };
      case 'cancelled':
        return { label: 'cancelled', className: 'bg-rose-100 text-rose-700 border-rose-300' };
      case 'published':
        return { label: 'published', className: 'bg-green-100 text-green-700 border-green-300' };
      default:
        return { label: normalized, className: 'bg-gray-100 text-gray-700 border-gray-300' };
    }
  };

  const statusMeta = getStatusMeta(activity.status);

  const currentLead = leads.length > 0 ? leads[0] : null;

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${isOpen ? 'shadow-md' : ''}`}>
      {/* Header Row — always shown */}
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 select-none"
        onClick={onToggle}
      >
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}

        {/* Sequence badge */}
        <span className="text-xs font-bold text-gray-400 w-6 text-center flex-shrink-0">
          {activity.sequence_order}
          {activity.parallel_group ? 'p' : ''}
        </span>

        {/* Title */}
        <span className="flex-1 font-medium text-gray-900 truncate">{activity.title}</span>

        {/* Time chip */}
        {activity.start_time && (
          <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
            <Clock className="h-3 w-3" />
            {formatTime(activity.start_time)}
          </span>
        )}

        {/* Owner */}
        {currentLead && (
          <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
            <User className="h-3 w-3" />
            {currentLead.name || currentLead.email || ''}
          </span>
        )}

        {/* Status */}
        <Badge variant="outline" className={`text-xs flex-shrink-0 ${statusMeta.className}`}>
          {statusMeta.label}
        </Badge>

        {/* Parallel indicator */}
        {activity.parallel_group && (
          <span title={t('activities:expandPanel.parallelTitle', { group: activity.parallel_group })} className="flex-shrink-0">
            <Layers className="h-3 w-3 text-purple-400" />
          </span>
        )}
      </div>

      {/* Expanded detail section */}
      {isOpen && (
        <div className="border-t bg-white px-4 py-4 space-y-4">
          {editing ? (
            /* Edit mode */
            <div className="space-y-3">
              {/* Title */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">{t('activities:expandPanel.labels.title')}</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-8 text-sm"
                  placeholder={t('activities:expandPanel.placeholders.activityName')}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Start time */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {t('activities:expandPanel.labels.startTimeOptional')}
                  </label>
                  <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                {/* End time */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {t('activities:expandPanel.labels.endTimeOptional')}
                  </label>
                  <input
                    type="datetime-local"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {t('activities:expandPanel.labels.locationOptional')}
                </label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="h-8 text-sm"
                  placeholder={t('activities:expandPanel.placeholders.location')}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                  <StickyNote className="h-3 w-3" /> {t('activities:expandPanel.labels.notesOptional')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  rows={2}
                  placeholder={t('activities:expandPanel.placeholders.notes')}
                />
              </div>

              {/* Owner (Lead) */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                  <User className="h-3 w-3" /> {t('activities:expandPanel.labels.ownerOptional')}
                </label>
                {loadingGuests ? (
                  <div className="text-xs text-gray-400">{t('activities:expandPanel.loadingGuests')}</div>
                ) : (
                  <select
                    value={selectedLeadId ?? ''}
                    onChange={(e) => setSelectedLeadId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
                  >
                    <option value="">{t('activities:expandPanel.unassignedOption')}</option>
                    {guests.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name || g.email || t('activities:expandPanel.guestFallback', { id: g.id })}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Parallel Group */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                  <Layers className="h-3 w-3" /> {t('activities:expandPanel.labels.parallelGroupOptional')}
                </label>
                <Input
                  value={parallelGroup}
                  onChange={(e) => setParallelGroup(e.target.value)}
                  className="h-8 text-sm"
                  placeholder={t('activities:expandPanel.placeholders.parallelGroup')}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t">
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {deleting ? t('activities:expandPanel.actions.deleting') : t('common:actions.delete')}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <X className="h-3 w-3 mr-1" />
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSave}
                    disabled={saving || !title.trim()}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    {saving ? t('common:status.saving') : t('common:actions.save')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* View mode */
            <div className="space-y-2">
              {(activity.start_time || activity.end_time) && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Clock className="h-4 w-4 text-gray-400" />
                  {activity.start_time ? formatTime(activity.start_time) : '—'}
                  {activity.end_time && ` → ${formatTime(activity.end_time)}`}
                </div>
              )}
              {activity.location && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {activity.location}
                </div>
              )}
              {activity.description && (
                <div className="flex items-start gap-1.5 text-sm text-gray-600">
                  <StickyNote className="h-4 w-4 text-gray-400 mt-0.5" />
                  {activity.description}
                </div>
              )}
              {currentLead && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <User className="h-4 w-4 text-gray-400" />
                  {currentLead.name || currentLead.email}
                </div>
              )}
              {activity.parallel_group && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Layers className="h-4 w-4 text-gray-400" />
                  {t('activities:expandPanel.parallelLabel', { group: activity.parallel_group })}
                </div>
              )}
              {!activity.start_time && !activity.location && !activity.description && !currentLead && !activity.parallel_group && (
                <p className="text-xs text-gray-400 italic">{t('activities:expandPanel.noDetailsYet')}</p>
              )}

              {canManage && (
                <div className="pt-2 border-t flex justify-end gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {deleting ? t('activities:expandPanel.actions.deleting') : t('common:actions.delete')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setEditing(true)}
                  >
                    {t('activities:expandPanel.actions.editDetails')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
